import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { take } from "rxjs/operators";
import { ApiService } from "src/app/services/api/api.service";
import moment from 'moment';
import 'moment-timezone';
import * as XLSX from 'xlsx';
import { cancel_event } from "./model";

@Injectable({
  providedIn: 'root',
})
export class FieldScheduleReportBLL {

  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isLoading$: Observable<boolean> = this.isLoading.asObservable();


  private sessions: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly sessions$: Observable<any> = this.sessions.asObservable();


  private invited_field_events: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly invited_field_events$: Observable<any> = this.invited_field_events.asObservable();

  private league_scheduler_events: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly league_scheduler_events$: Observable<any> = this.league_scheduler_events.asObservable();


  private last_params: any = null;

  constructor(
    private api: ApiService
  ) { }

  // this.referee.id,
  // this.game_date,
  // this.bus_service.id,
  // this.assigned_name,
  // this.opposing_team_name,
  // this.sport.id,
  // this.field_facility.id,
  // this.event_type,
  // this.game_type
  get_sessions(referee_id = '', game_date = '', game_date_to = '', bus_service_id = '', assigned_name = '', opposing_team_name = '', sport_id = '', field_facility_id = '', event_type = '', game_type = '', is_ad_confirmed = false, is_bus_confirmed = false, is_referee_confirmed = false, admin: any = null, is_practice: boolean = false) {

    if (arguments.length === 0 && this.last_params) {
      return this.get_sessions(
        this.last_params.referee_id,
        this.last_params.game_date,
        this.last_params.game_date_to,
        this.last_params.bus_service_id,
        this.last_params.assigned_name,
        this.last_params.opposing_team_name,
        this.last_params.sport_id,
        this.last_params.field_facility_id,
        this.last_params.event_type,
        this.last_params.game_type,
        this.last_params.is_ad_confirmed,
        this.last_params.is_bus_confirmed,
        this.last_params.is_referee_confirmed,
        this.last_params.admin,
        this.last_params.is_practice
      );
    }

    this.last_params = {
      referee_id,
      game_date,
      game_date_to,
      bus_service_id,
      assigned_name,
      opposing_team_name,
      sport_id,
      field_facility_id,
      event_type,
      game_type,
      is_ad_confirmed,
      is_bus_confirmed,
      is_referee_confirmed,
      admin,
      is_practice
    };

    this.isLoading.next(true);
    let url = `field-events?type=get_all_field_events&timezone=${moment.tz.guess()}&is_practice=${is_practice}`;

    if (referee_id) {
      url += `&referee_id=${referee_id}`;
    }

    if (is_ad_confirmed) {
      url += `&is_ad_confirmed=${is_ad_confirmed}`;
    }

    if (is_bus_confirmed) {
      url += `&is_bus_confirmed=${is_bus_confirmed}`;
    }

    if (is_referee_confirmed) {
      url += `&referee_confirmed=${is_referee_confirmed}`;
    }

    if (game_date) {
      url += `&date_of_game_events=${moment(game_date).format('YYYY-MM-DD')}`;
    }
    // else {
    //   // If no game_date is provided, default to today's date
    //   url += `&date_of_game_events=${moment().format('YYYY-MM-DD')}`;
    // }

    if (game_date_to) {
      url += `&end_date_of_game_events=${moment(game_date_to).format('YYYY-MM-DD')}`;
    }

    if (bus_service_id) {
      url += `&bus_service_id=${bus_service_id}`;
    }

    if (assigned_name) {
      url += `&assigned_name=${assigned_name}`;
    }

    if (opposing_team_name) {
      url += `&opposing_team_name=${opposing_team_name}`;
    }

    if (sport_id) {
      url += `&sport_id=${sport_id}`;
    }

    if (field_facility_id) {
      url += `&field_id=${field_facility_id}`;
    }

    if (event_type) {
      url += `&event_type=${event_type}`;
    }

    if (game_type) {
      url += `&game_type=${game_type}`;
    }

    if (admin?.ad_admin_id) {
      url += `&admin_id=${admin.ad_admin_id}`;
    }

    if (admin?.ad_admin_email) {
      url += `&admin_email=${admin.ad_admin_email}`;
    }

    this.api.get(url).pipe(take(1)).subscribe((res: any) => {

      this.sessions.next(res.field_events?.filter((e)=> e.field_event_type !== 'practice'));

      this.invited_field_events.next(res.invited_field_events);

      this.league_scheduler_events.next((res.league_scheduler_events || []));

      this.isLoading.next(false);
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  send_submit_game_email(event: any) {
    this.isLoading.next(true);
    let url = `field-events?type=submit_game`;

    this.api.patch(url, event).pipe(take(1)).subscribe((res: any) => {

      this.get_sessions();
      this.isLoading.next(false);
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  send_cancel_game_email(event: cancel_event) {
    this.isLoading.next(true);
    let url = `field-events?type=cancel_game`;

    this.api.patch(url, event).pipe(take(1)).subscribe((res: any) => {
         // Pass the date_of_game_event to get_sessions so it includes in the query params
      // Use the event's date_of_game_event directly, ensuring it's a valid date string
      let gameDate = '';
      if (event.date_of_game_event && moment(event.date_of_game_event).isValid()) {
        gameDate = moment(event.date_of_game_event).format('YYYY-MM-DD');
      }
      this.get_sessions('', gameDate);
      this.isLoading.next(false);
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  exportToExcel(jsonData: any[], fileName: string): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }

}